import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Tooltip, CircularProgress,
} from '@mui/material';
import apiClient from '../api/client';

const CELL = { color: '#94a3b8', borderColor: '#1e293b' };
const HEAD_CELL = { color: '#64748b', borderColor: '#1e293b', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' };

function StatCard({ label, value, sub }) {
  return (
    <Paper sx={{ bgcolor: '#1e293b', p: 3, borderRadius: 2 }}>
      <Typography variant="h3" fontWeight={700} sx={{ color: '#f1f5f9' }}>{value ?? '—'}</Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>{label}</Typography>
      {sub && <Typography variant="caption" sx={{ color: '#64748b' }}>{sub}</Typography>}
    </Paper>
  );
}

function RoutingBreakdown({ breakdown }) {
  if (!breakdown || !Object.keys(breakdown).length) return null;
  const colors = { tomtom: '#3b82f6', traveltime: '#8b5cf6', osrm: '#10b981', haversine: '#f59e0b', unknown: '#64748b' };
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      {Object.entries(breakdown).map(([src, count]) => (
        <Chip
          key={src}
          label={`${src}: ${count}`}
          size="small"
          sx={{ bgcolor: colors[src] ?? '#64748b', color: '#fff', fontWeight: 600 }}
        />
      ))}
    </Box>
  );
}

function SendInviteForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      await apiClient.post('/admin/invites', { email });
      setResult({ type: 'success', message: `Invite sent to ${email}` });
      setEmail('');
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.error || 'Failed to send invite' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ bgcolor: '#1e293b', p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={600} sx={{ color: '#f1f5f9', mb: 1 }}>Send Invite</Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
        Send a registration link to a new user's email address.
      </Typography>
      {result && <Alert severity={result.type} sx={{ mb: 2 }}>{result.message}</Alert>}
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          size="small"
          sx={{ flexGrow: 1, input: { color: '#f1f5f9' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#334155' } } }}
        />
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? 'Sending…' : 'Send Invite'}
        </Button>
      </Box>
    </Paper>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function UserActivityTable({ rows }) {
  if (!rows?.length) return <Typography sx={{ color: '#64748b', mt: 2 }}>No users yet.</Typography>;
  return (
    <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Name', 'Email', 'Joined', 'Last Login', 'Logins', 'Plans Run', 'Properties'].map(h => (
              <TableCell key={h} sx={HEAD_CELL}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(u => (
            <TableRow key={u.id} hover sx={{ '&:hover': { bgcolor: '#0f172a' } }}>
              <TableCell sx={{ ...CELL, color: '#f1f5f9', fontWeight: 500 }}>{u.name}</TableCell>
              <TableCell sx={CELL}>{u.email}</TableCell>
              <TableCell sx={CELL}>{fmtDate(u.joined_at)}</TableCell>
              <TableCell sx={CELL}>
                <Tooltip title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}>
                  <span>{u.last_login_at ? fmtDate(u.last_login_at) : <em style={{ color: '#475569' }}>Never</em>}</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={CELL}>{u.logins}</TableCell>
              <TableCell sx={CELL}>{u.plans}</TableCell>
              <TableCell sx={CELL}>{u.properties}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LoginHistoryTable({ events }) {
  if (!events?.length) return <Typography sx={{ color: '#64748b', mt: 2 }}>No login events recorded yet.</Typography>;
  return (
    <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Time', 'User', 'IP', 'Client'].map(h => (
              <TableCell key={h} sx={HEAD_CELL}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map(ev => (
            <TableRow key={ev.id} hover sx={{ '&:hover': { bgcolor: '#0f172a' } }}>
              <TableCell sx={CELL}>{fmtDateTime(ev.createdAt)}</TableCell>
              <TableCell sx={{ ...CELL, color: '#f1f5f9' }}>
                {ev.user ? `${ev.user.first_name} ${ev.user.last_name}` : '—'}
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{ev.user?.email}</Typography>
              </TableCell>
              <TableCell sx={CELL}>{ev.ip || '—'}</TableCell>
              <TableCell sx={{ ...CELL, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Tooltip title={ev.user_agent || ''}>
                  <span>{ev.user_agent ? ev.user_agent.split(' ')[0] : '—'}</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const SOURCE_COLORS = { tomtom: '#3b82f6', traveltime: '#8b5cf6', osrm: '#10b981', haversine: '#f59e0b', unknown: '#64748b' };

function ApiUsageSection({ usage }) {
  if (!usage || !usage.totals?.length) return null;
  const { totals, daily, byOrg } = usage;

  const totalCalls = totals.reduce((s, r) => s + r.count, 0);
  const maxCount = Math.max(...totals.map(r => r.count), 1);

  // Group daily rows by date
  const byDay = {};
  for (const row of daily) {
    const d = row.day.slice(0, 10);
    if (!byDay[d]) byDay[d] = {};
    byDay[d][row.routing_source] = row.count;
  }
  const days = Object.keys(byDay).slice(0, 14); // last 14 days

  // Group byOrg
  const orgMap = {};
  for (const row of byOrg) {
    if (!orgMap[row.org_name]) orgMap[row.org_name] = {};
    orgMap[row.org_name][row.routing_source] = row.count;
  }

  return (
    <>
      <Typography variant="h6" fontWeight={600} sx={{ color: '#f1f5f9', mb: 2 }}>Routing API Usage</Typography>
      <Grid container spacing={3} mb={3}>
        {totals.map(({ source, count }) => (
          <Grid item xs={6} sm={3} key={source}>
            <Paper sx={{ bgcolor: '#1e293b', p: 2, borderRadius: 2, borderLeft: `3px solid ${SOURCE_COLORS[source] ?? '#64748b'}` }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#f1f5f9' }}>{count}</Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', textTransform: 'capitalize' }}>{source}</Typography>
              <Box sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: '#0f172a' }}>
                <Box sx={{ height: '100%', width: `${Math.round((count / maxCount) * 100)}%`, borderRadius: 2, bgcolor: SOURCE_COLORS[source] ?? '#64748b' }} />
              </Box>
              <Typography variant="caption" sx={{ color: '#475569' }}>{totalCalls ? Math.round((count / totalCalls) * 100) : 0}% of total</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {days.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em' }}>Last 14 Days</Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', borderRadius: 2, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL}>Date</TableCell>
                  {['tomtom', 'traveltime', 'osrm', 'haversine'].map(s => (
                    <TableCell key={s} sx={{ ...HEAD_CELL, color: SOURCE_COLORS[s] }}>{s}</TableCell>
                  ))}
                  <TableCell sx={HEAD_CELL}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map(d => {
                  const row = byDay[d];
                  const rowTotal = Object.values(row).reduce((s, v) => s + v, 0);
                  return (
                    <TableRow key={d} hover sx={{ '&:hover': { bgcolor: '#0f172a' } }}>
                      <TableCell sx={{ ...CELL, color: '#f1f5f9' }}>{d}</TableCell>
                      {['tomtom', 'traveltime', 'osrm', 'haversine'].map(s => (
                        <TableCell key={s} sx={CELL}>{row[s] ?? '—'}</TableCell>
                      ))}
                      <TableCell sx={{ ...CELL, fontWeight: 600 }}>{rowTotal}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {Object.keys(orgMap).length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em' }}>By Organization</Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', borderRadius: 2, mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL}>Organization</TableCell>
                  {['tomtom', 'traveltime', 'osrm', 'haversine'].map(s => (
                    <TableCell key={s} sx={{ ...HEAD_CELL, color: SOURCE_COLORS[s] }}>{s}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(orgMap).map(([org, counts]) => (
                  <TableRow key={org} hover sx={{ '&:hover': { bgcolor: '#0f172a' } }}>
                    <TableCell sx={{ ...CELL, color: '#f1f5f9', fontWeight: 500 }}>{org}</TableCell>
                    {['tomtom', 'traveltime', 'osrm', 'haversine'].map(s => (
                      <TableCell key={s} sx={CELL}>{counts[s] ?? '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get('/admin/stats'),
      apiClient.get('/admin/activity'),
      apiClient.get('/admin/api-usage'),
    ]).then(([s, a, u]) => {
      setStats(s.data.data);
      setActivity(a.data.data);
      setApiUsage(u.data.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={1}>Admin Overview</Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4 }}>Platform-wide summary</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Organizations" value={stats?.orgs} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Users" value={stats?.users} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Routes Optimized" value={stats?.plans} sub={<RoutingBreakdown breakdown={stats?.routingBreakdown} />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Properties" value={stats?.properties} sub={`${stats?.recentLogins ?? 0} logins last 30 days`} />
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={600} sx={{ color: '#f1f5f9', mb: 2 }}>User Activity</Typography>
          <Box mb={4}>
            <UserActivityTable rows={activity?.activity} />
          </Box>

          <Typography variant="h6" fontWeight={600} sx={{ color: '#f1f5f9', mb: 2 }}>Recent Logins</Typography>
          <Box mb={4}>
            <LoginHistoryTable events={activity?.recentEvents} />
          </Box>

          <ApiUsageSection usage={apiUsage} />

          <SendInviteForm />
        </>
      )}
    </Box>
  );
}
